/**
 * Sidebar step subtitles (#1094 / S2): `N/M` field progress per Create Request step.
 */

import { getFacilityPowerFamilyStepFieldProgress } from './facilityPowerStepProgress';
import {
  filterSubsectionFieldsForFormView,
  filterSummaryFieldMappingsForFormView,
  type FormFieldVisibilityContext,
} from './formFieldVisibility';
import { countSubsectionFields } from './subsectionHeaderMetrics';
import { hasLegacyCoolingSystemRequestFields } from '../components/coolingsystems/coolingSystemListUtils';
import { hasLegacyLargeUpsRequestFields } from '../components/largeups/largeUpsListUtils';
import { hasLegacyLiquidCoolingCduRequestFields } from '../components/liquidcoolingcdus/liquidCoolingCduListUtils';
import { hasLegacyPowerDistributionRequestFields } from '../components/powerdistribution/powerDistributionListUtils';
import { hasLegacyRackRequestFields } from '../components/racks/rackListUtils';
import { hasLegacySmallUpsRequestFields } from '../components/smallups/smallUpsListUtils';
import {
  getCoolingReadinessSummaryFieldMappings,
  getLiquidCoolingSummaryFieldMappings,
  getOpportunitySummaryFieldMappingsFromRegistry,
  getSiteInformationSummaryFieldMappings,
  getSmallUpsSummaryFieldMappings,
} from '../request/fields/requestFieldRegistry';
import { getCoolingSystemDescriptorSummaryDefinitions } from '../request/sections/coolingSystemDescriptorSummary';
import { getLargeUpsDescriptorSummaryDefinitions } from '../request/sections/largeUpsDescriptorSummary';
import { getLiquidCoolingDescriptorSummaryDefinitions } from '../request/sections/liquidCoolingDescriptorSummary';
import { getPowerDistributionDescriptorSummaryDefinitions } from '../request/sections/powerDistributionDescriptorSummary';
import { getRacksDescriptorSummaryDefinitions } from '../request/sections/racksDescriptorSummary';
import { getSmallUpsDescriptorSummaryDefinitions } from '../request/sections/smallUpsDescriptorSummary';
// #1149 Issue 4: the same per-section legacy-flat-field checks the display list
// uses to synthesize a fallback unit, reused so the nav count matches the list.

import type { CoolingSystemConfiguration } from '../types/coolingSystem';
import type { LargeUpsConfiguration } from '../types/largeUps';
import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';
import type { PowerDistributionConfiguration } from '../types/powerDistribution';
import type { RackConfiguration } from '../types/rack';
import type { SmallUpsConfiguration } from '../types/smallUps';

type StepFieldMapping = { dataField: string };

export type StepFieldProgressOptions = {
  enabledStepIds?: readonly string[];
  storedStepIds?: string[] | null;
  stage?: string | null;
  visibility?: FormFieldVisibilityContext;
};

const STEP_FIELD_MAPPINGS: Partial<
  Record<string, () => readonly StepFieldMapping[]>
> = {
  opportunity: getOpportunitySummaryFieldMappingsFromRegistry,
  'site-information': getSiteInformationSummaryFieldMappings,
  'small-ups': getSmallUpsSummaryFieldMappings,
  'cooling-readiness': getCoolingReadinessSummaryFieldMappings,
  'liquid-cooling': getLiquidCoolingSummaryFieldMappings,
};

function rackConfigurationsFromValues(
  values: Record<string, unknown>,
): RackConfiguration[] | undefined {
  const racks = values.rackConfigurations;
  if (!Array.isArray(racks) || racks.length === 0) {
    return undefined;
  }
  return racks as RackConfiguration[];
}

export function getRackPowerStepFieldProgress(
  values: Record<string, unknown> | null | undefined,
  visibility?: FormFieldVisibilityContext,
): { filled: number; total: number } | null {
  if (!values) {
    return null;
  }
  const racks = rackConfigurationsFromValues(values);
  if (!racks || racks.length === 0) {
    return null;
  }
  const fields = filterSubsectionFieldsForFormView(
    getRacksDescriptorSummaryDefinitions().map(({ dataField, label }) => ({
      key: dataField,
      shortLabel: label,
    })),
    visibility,
  );
  if (fields.length === 0) {
    return null;
  }
  const filled = racks.reduce(
    (sum, rack) =>
      sum +
      countSubsectionFields(fields, rack as unknown as Record<string, unknown>)
        .filled,
    0,
  );
  return {
    filled,
    total: fields.length * racks.length,
  };
}

function liquidCoolingCduConfigurationsFromValues(
  values: Record<string, unknown>,
): LiquidCoolingCduConfiguration[] | undefined {
  const units = values.liquidCoolingCduConfigurations;
  if (!Array.isArray(units) || units.length === 0) {
    return undefined;
  }
  return units as LiquidCoolingCduConfiguration[];
}

function largeUpsConfigurationsFromValues(
  values: Record<string, unknown>,
): LargeUpsConfiguration[] | undefined {
  const units = values.largeUpsConfigurations;
  if (!Array.isArray(units) || units.length === 0) {
    return undefined;
  }
  return units as LargeUpsConfiguration[];
}

function powerDistributionConfigurationsFromValues(
  values: Record<string, unknown>,
): PowerDistributionConfiguration[] | undefined {
  const units = values.powerDistributionConfigurations;
  if (!Array.isArray(units) || units.length === 0) {
    return undefined;
  }
  return units as PowerDistributionConfiguration[];
}

function smallUpsConfigurationsFromValues(
  values: Record<string, unknown>,
): SmallUpsConfiguration[] | undefined {
  const units = values.smallUpsConfigurations;
  if (!Array.isArray(units) || units.length === 0) {
    return undefined;
  }
  return units as SmallUpsConfiguration[];
}

function coolingSystemConfigurationsFromValues(
  values: Record<string, unknown>,
): CoolingSystemConfiguration[] | undefined {
  const units = values.coolingSystemConfigurations;
  if (!Array.isArray(units) || units.length === 0) {
    return undefined;
  }
  return units as CoolingSystemConfiguration[];
}

/**
 * Sidebar Large UPS step field progress — mirrors the LC branch (Story 4.2)
 * using the descriptor-driven Summary source so the sidebar badge count stays
 * aligned with the Summary row set (Story 4.3 Bug 1 fix path). Only fires when
 * the request has one or more `largeUpsConfigurations`; without units, the LU
 * step falls through to `getFacilityPowerFamilyStepFieldProgress`'s request-
 * level count (unchanged from pre-refactor).
 */
export function getLargeUpsStepFieldProgress(
  values: Record<string, unknown> | null | undefined,
  visibility?: FormFieldVisibilityContext,
): { filled: number; total: number } | null {
  if (!values) {
    return null;
  }
  const units = largeUpsConfigurationsFromValues(values);
  if (!units || units.length === 0) {
    return null;
  }
  const fields = filterSubsectionFieldsForFormView(
    getLargeUpsDescriptorSummaryDefinitions().map(({ dataField, label }) => ({
      key: dataField,
      shortLabel: label,
    })),
    visibility,
  );
  if (fields.length === 0) {
    return null;
  }
  const filled = units.reduce(
    (sum, unit) =>
      sum +
      countSubsectionFields(fields, unit as unknown as Record<string, unknown>)
        .filled,
    0,
  );
  return {
    filled,
    total: fields.length * units.length,
  };
}

/**
 * Sidebar Power Distribution step field progress — mirrors the LU branch
 * (Story 4.3) using the descriptor-driven Summary source so the sidebar badge
 * count stays aligned with the Summary row set. Only fires when the request
 * has one or more `powerDistributionConfigurations`; without units, the PD
 * step falls through to `getFacilityPowerFamilyStepFieldProgress`'s request-
 * level count (unchanged from pre-refactor).
 */
export function getPowerDistributionStepFieldProgress(
  values: Record<string, unknown> | null | undefined,
  visibility?: FormFieldVisibilityContext,
): { filled: number; total: number } | null {
  if (!values) {
    return null;
  }
  const units = powerDistributionConfigurationsFromValues(values);
  if (!units || units.length === 0) {
    return null;
  }
  const fields = filterSubsectionFieldsForFormView(
    getPowerDistributionDescriptorSummaryDefinitions().map(
      ({ dataField, label }) => ({
        key: dataField,
        shortLabel: label,
      }),
    ),
    visibility,
  );
  if (fields.length === 0) {
    return null;
  }
  const filled = units.reduce(
    (sum, unit) =>
      sum +
      countSubsectionFields(fields, unit as unknown as Record<string, unknown>)
        .filled,
    0,
  );
  return {
    filled,
    total: fields.length * units.length,
  };
}

/**
 * Sidebar Small UPS step field progress — mirrors the PD branch (Story 4.4)
 * using the descriptor-driven Summary source so the sidebar badge count stays
 * aligned with the Summary row set. Only fires when the request has one or
 * more `smallUpsConfigurations`; without units, the SU step falls through to
 * the existing `STEP_FIELD_MAPPINGS['small-ups']` request-level path
 * (`getSmallUpsSummaryFieldMappings`) — SU is NOT a facility-power family
 * child, so its no-units fall-through is its own step-level mapping rather
 * than `getFacilityPowerFamilyStepFieldProgress` (LU / PD pattern).
 */
export function getSmallUpsStepFieldProgress(
  values: Record<string, unknown> | null | undefined,
  visibility?: FormFieldVisibilityContext,
): { filled: number; total: number } | null {
  if (!values) {
    return null;
  }
  const units = smallUpsConfigurationsFromValues(values);
  if (!units || units.length === 0) {
    return null;
  }
  const fields = filterSubsectionFieldsForFormView(
    getSmallUpsDescriptorSummaryDefinitions().map(({ dataField, label }) => ({
      key: dataField,
      shortLabel: label,
    })),
    visibility,
  );
  if (fields.length === 0) {
    return null;
  }
  const filled = units.reduce(
    (sum, unit) =>
      sum +
      countSubsectionFields(fields, unit as unknown as Record<string, unknown>)
        .filled,
    0,
  );
  return {
    filled,
    total: fields.length * units.length,
  };
}

/**
 * Sidebar Cooling System step field progress — mirrors the SU branch
 * (Story 4.5) using the descriptor-driven Summary source so the sidebar badge
 * count stays aligned with the Summary row set. Only fires when the request
 * has one or more `coolingSystemConfigurations`; without units, the Cooling
 * step falls through to the existing
 * `STEP_FIELD_MAPPINGS['cooling-readiness']` request-level path
 * (`getCoolingReadinessSummaryFieldMappings`) — Cooling is NOT a
 * facility-power family child, so its no-units fall-through is its own
 * step-level mapping rather than `getFacilityPowerFamilyStepFieldProgress`
 * (LC / SU pattern).
 */
export function getCoolingSystemStepFieldProgress(
  values: Record<string, unknown> | null | undefined,
  visibility?: FormFieldVisibilityContext,
): { filled: number; total: number } | null {
  if (!values) {
    return null;
  }
  const units = coolingSystemConfigurationsFromValues(values);
  if (!units || units.length === 0) {
    return null;
  }
  const fields = filterSubsectionFieldsForFormView(
    getCoolingSystemDescriptorSummaryDefinitions().map(
      ({ dataField, label }) => ({
        key: dataField,
        shortLabel: label,
      }),
    ),
    visibility,
  );
  if (fields.length === 0) {
    return null;
  }
  const filled = units.reduce(
    (sum, unit) =>
      sum +
      countSubsectionFields(fields, unit as unknown as Record<string, unknown>)
        .filled,
    0,
  );
  return {
    filled,
    total: fields.length * units.length,
  };
}

/**
 * Sidebar Liquid Cooling step field progress — mirrors the Racks branch
 * (Story 3.3) using the descriptor-driven Summary source so the sidebar badge
 * count stays byte-identical to the Summary row set.
 */
export function getLiquidCoolingStepFieldProgress(
  values: Record<string, unknown> | null | undefined,
  visibility?: FormFieldVisibilityContext,
): { filled: number; total: number } | null {
  if (!values) {
    return null;
  }
  const units = liquidCoolingCduConfigurationsFromValues(values);
  if (!units || units.length === 0) {
    return null;
  }
  const fields = filterSubsectionFieldsForFormView(
    getLiquidCoolingDescriptorSummaryDefinitions().map(
      ({ dataField, label }) => ({
        key: dataField,
        shortLabel: label,
      }),
    ),
    visibility,
  );
  if (fields.length === 0) {
    return null;
  }
  const filled = units.reduce(
    (sum, unit) =>
      sum +
      countSubsectionFields(fields, unit as unknown as Record<string, unknown>)
        .filled,
    0,
  );
  return {
    filled,
    total: fields.length * units.length,
  };
}

export function getStepFieldProgress(
  stepId: string,
  values: Record<string, unknown> | null | undefined,
  options?: StepFieldProgressOptions,
): { filled: number; total: number } | null {
  if (!values) {
    return null;
  }
  if (stepId === 'rack-power') {
    return getRackPowerStepFieldProgress(values, options?.visibility);
  }
  if (stepId === 'liquid-cooling') {
    const perUnit = getLiquidCoolingStepFieldProgress(
      values,
      options?.visibility,
    );
    if (perUnit) {
      return perUnit;
    }
    // Falls through to request-level LC mapping when no CDUs are present.
  }
  if (stepId === 'large-ups') {
    const perUnit = getLargeUpsStepFieldProgress(values, options?.visibility);
    if (perUnit) {
      return perUnit;
    }
    // Falls through to the facility-power family branch when no LU units are
    // present (unchanged from pre-refactor behavior).
  }
  if (stepId === 'power-distribution') {
    const perUnit = getPowerDistributionStepFieldProgress(
      values,
      options?.visibility,
    );
    if (perUnit) {
      return perUnit;
    }
    // Falls through to the facility-power family branch when no PD units are
    // present (unchanged from pre-refactor behavior).
  }
  if (stepId === 'small-ups') {
    const perUnit = getSmallUpsStepFieldProgress(values, options?.visibility);
    if (perUnit) {
      return perUnit;
    }
    // Falls through to the existing STEP_FIELD_MAPPINGS['small-ups'] request-
    // level path (`getSmallUpsSummaryFieldMappings`) when no SU units are
    // present. SU is NOT a facility-power family child, so it falls through
    // to its own step-level mapping (mirrors the LC pattern), NOT the family
    // branch. `STEP_FIELD_MAPPINGS[43]` is preserved for exactly this
    // fall-through.
  }
  if (stepId === 'cooling-readiness') {
    const perUnit = getCoolingSystemStepFieldProgress(
      values,
      options?.visibility,
    );
    if (perUnit) {
      return perUnit;
    }
    // Falls through to the existing STEP_FIELD_MAPPINGS['cooling-readiness']
    // request-level path (`getCoolingReadinessSummaryFieldMappings`) when no
    // Cooling units are present. Cooling is NOT a facility-power family
    // child, so it falls through to its own step-level mapping (mirrors the
    // LC / SU pattern), NOT the family branch. `STEP_FIELD_MAPPINGS[46]` is
    // preserved for exactly this fall-through.
  }
  if (
    stepId === 'facility-power' ||
    stepId === 'large-ups' ||
    stepId === 'power-distribution'
  ) {
    return getFacilityPowerFamilyStepFieldProgress(stepId, values, options);
  }
  const getter = STEP_FIELD_MAPPINGS[stepId];
  if (!getter) {
    return null;
  }
  const visibleMappings = filterSummaryFieldMappingsForFormView(
    getter() as Array<{
      dataField: string;
      permissionSection: string;
      permissionField: string;
    }>,
    options?.visibility,
  );
  const fields = visibleMappings.map(({ dataField }) => ({
    key: dataField,
    shortLabel: dataField,
  }));
  if (fields.length === 0) {
    return null;
  }
  return countSubsectionFields(fields, values);
}

export function formatStepFieldProgressSubtitle(
  filled: number,
  total: number,
): string {
  return `${filled}/${total}`;
}

/** Spoken form for screen readers — matches `FieldFillCountBadge`'s label. */
export function formatStepFieldProgressAccessibilityLabel(
  filled: number,
  total: number,
): string {
  return `${filled} of ${total} fields entered`;
}

export function formatRackUnitSubtitle(count: number): string {
  return count === 1 ? '1 unit added' : `${count} units added`;
}

/** Repeatable child-table steps that show `N units added` instead of field progress. */
export const REPEATABLE_UNIT_CONFIG_KEYS: Record<string, string> = {
  'rack-power': 'rackConfigurations',
  'large-ups': 'largeUpsConfigurations',
  'power-distribution': 'powerDistributionConfigurations',
  'small-ups': 'smallUpsConfigurations',
  'cooling-readiness': 'coolingSystemConfigurations',
  'liquid-cooling': 'liquidCoolingCduConfigurations',
};

/** Per-step check mirroring the display list's legacy fallback-unit synthesis (#1149 Issue 4). */
const REPEATABLE_UNIT_LEGACY_CHECK: Record<
  string,
  (source: Record<string, unknown>) => boolean
> = {
  'rack-power': hasLegacyRackRequestFields,
  'large-ups': hasLegacyLargeUpsRequestFields,
  'power-distribution': hasLegacyPowerDistributionRequestFields,
  'small-ups': hasLegacySmallUpsRequestFields,
  'cooling-readiness': hasLegacyCoolingSystemRequestFields,
  'liquid-cooling': hasLegacyLiquidCoolingCduRequestFields,
};

export function isRepeatableUnitStep(stepId: string): boolean {
  return stepId in REPEATABLE_UNIT_CONFIG_KEYS;
}

/** Legacy `facilityPowerInstances` row count (pre-#997 inline instances). */
export function getFacilityPowerUnitCount(
  values: Record<string, unknown> | null | undefined,
): number {
  return (
    getDraftInstanceCountFromFormValues('facilityPowerInstances', values) ?? 0
  );
}

export function resolveFacilityPowerUnitSubtitleCount(options: {
  formValues?: Record<string, unknown> | null;
}): number {
  return getFacilityPowerUnitCount(options.formValues);
}

/** Draft create / in-memory instance count from form or request snapshot. */
export function getDraftInstanceCountFromFormValues(
  instancesKey: string,
  values: Record<string, unknown> | null | undefined,
): number | undefined {
  const instances = values?.[instancesKey];
  if (!Array.isArray(instances)) {
    return undefined;
  }
  return instances.length;
}

/**
 * Sidebar unit count for a repeatable step — prefers child-table query when loaded,
 * but falls back to #1154 instance arrays (and nested config arrays) in form/cache
 * so counts update immediately after Save before the child query refetches.
 */
export function resolveRepeatableUnitSubtitleCount(
  stepId: string,
  options: {
    persistedChildTableCount?: number;
    formValues?: Record<string, unknown> | null;
  },
): number {
  const configKey = REPEATABLE_UNIT_CONFIG_KEYS[stepId];
  if (!configKey) {
    return 0;
  }

  const sessionCount =
    getDraftInstanceCountFromFormValues(configKey, options.formValues) ?? 0;

  const baseCount =
    options.persistedChildTableCount === undefined
      ? sessionCount
      : Math.max(options.persistedChildTableCount, sessionCount);

  if (baseCount > 0) {
    return baseCount;
  }

  // #1149 Issue 4: with no child rows and no draft instances, the display list
  // still renders one synthesized fallback unit when the section has legacy flat
  // request data — count it so the nav reads "1 unit added" instead of "0 units
  // added". Uses the same per-section legacy check as the list synthesis, so
  // count and list stay in lockstep (readiness-only data does not trigger it).
  const hasLegacy = REPEATABLE_UNIT_LEGACY_CHECK[stepId];
  if (
    hasLegacy &&
    options.formValues &&
    hasLegacy(options.formValues as Record<string, unknown>)
  ) {
    return 1;
  }

  return baseCount;
}

/** Draft create flow: rack count from form state (updates before save). */
export function getDraftRackCountFromFormValues(
  values: Record<string, unknown> | null | undefined,
): number | undefined {
  return getDraftInstanceCountFromFormValues('rackConfigurations', values);
}
